import { forwardRef } from "react";
import formatCI from "../../utils/formatCI";
import useIsMobile from "../../hooks/useIsMobile";
import { CONF_OPTIONS } from "./steps/DetaliiStep";

function fmtDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

function fmtDateFromISO(iso) {
  if (!iso) return "___________";
  const [y, mo, d] = iso.split("-");
  return `${d}/${mo}/${y}`;
}

const durataObligatieLabels = { "1": "1 (un) an", "2": "2 (doi) ani", "3": "3 (trei) ani", "5": "5 (cinci) ani" };
const durataAcordLabels = { colaborare: "pe durata colaborarii dintre Parti", "1": "1 (un) an de la data semnarii", "2": "2 (doi) ani de la data semnarii", nedeterminata: "pe durata nedeterminata" };

function toRoman(num) {
  const map = [[10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]];
  let result = "";
  for (const [value, numeral] of map) {
    while (num >= value) { result += numeral; num -= value; }
  }
  return result;
}

function getPartyName(data, prefix) {
  if (data[`${prefix}Tip`] === "pf") return data[`${prefix}Nume`] || "________________________";
  if (data[`${prefix}Tip`] === "pfa") return data[`${prefix}Denumire`] || "________________________";
  if (data[`${prefix}Tip`] === "srl") return data[`${prefix}Denumire`] || "________________________";
  return "________________________";
}

const ContractPreview = forwardRef(function ContractPreview({ data }, ref) {
  const isMobile = useIsMobile();
  const signDate = data.dataSemnare ? fmtDateFromISO(data.dataSemnare) : fmtDate(new Date());

  const p1Label = data.bilateral ? "Prima Parte" : "Partea care dezvaluie";
  const p2Label = data.bilateral ? "A Doua Parte" : "Partea care primeste";

  const sectionTitle = () => ({
    fontSize: "15px",
    fontWeight: 700,
    fontFamily: "'Playfair Display', serif",
    color: "#2d2418",
    margin: "28px 0 12px 0",
    paddingBottom: "6px",
    borderBottom: "1px solid #e8e0d4",
    letterSpacing: "0.03em",
    pageBreakAfter: "avoid",
    breakAfter: "avoid",
  });

  const p = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "0 0 8px 0",
    textAlign: "justify",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  };

  const indent = { ...p, paddingLeft: isMobile ? "12px" : "20px" };
  const bold = { fontWeight: 700, color: "#2d2418" };

  const fieldRow = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "2px 0",
    paddingLeft: isMobile ? "12px" : "20px",
  };

  function renderParty(prefix) {
    const tip = data[`${prefix}Tip`];
    if (tip === "pf") {
      const ciVal = data[`${prefix}CI`] ? formatCI(data[`${prefix}CI`]) : "___ ___";
      return (
        <>
          <p style={fieldRow}>Nume: <span style={bold}>{data[`${prefix}Nume`] || "________________________"}</span></p>
          <p style={fieldRow}>Domiciliul: <span style={bold}>{data[`${prefix}Adresa`] || "________________________"}</span></p>
          {data[`${prefix}CNP`] && <p style={fieldRow}>CNP: <span style={bold}>{data[`${prefix}CNP`]}</span></p>}
          {data[`${prefix}CI`] && <p style={fieldRow}>CI seria <span style={bold}>{ciVal}</span></p>}
        </>
      );
    }
    if (tip === "pfa") {
      return (
        <>
          <p style={fieldRow}>PFA <span style={bold}>{data[`${prefix}Denumire`] || "________________________"}</span>, CUI <span style={bold}>{data[`${prefix}CUI`] || "________________________"}</span>, cu sediul profesional in <span style={bold}>{data[`${prefix}Sediu`] || "________________________"}</span></p>
          <p style={fieldRow}>Reprezentat prin <span style={bold}>{data[`${prefix}Titular`] || "________________________"}</span>{data[`${prefix}CNP`] && <>, CNP <span style={bold}>{data[`${prefix}CNP`]}</span></>}</p>
        </>
      );
    }
    if (tip === "srl") {
      return (
        <>
          <p style={fieldRow}>Denumire: <span style={bold}>{data[`${prefix}Denumire`] || "________________________"}</span></p>
          <p style={fieldRow}>CUI: <span style={bold}>{data[`${prefix}CUI`] || "________________________"}</span></p>
          {data[`${prefix}RegCom`] && <p style={fieldRow}>Nr. Reg. Com.: <span style={bold}>{data[`${prefix}RegCom`]}</span></p>}
          <p style={fieldRow}>Sediu social: <span style={bold}>{data[`${prefix}Sediu`] || "________________________"}</span></p>
          <p style={fieldRow}>Reprezentant legal: <span style={bold}>{data[`${prefix}Reprezentant`] || "________________________"}</span></p>
        </>
      );
    }
    return null;
  }

  function getConfidentialItems() {
    const items = [];
    CONF_OPTIONS.forEach((opt) => { if (data[opt.key]) items.push(opt.label.toLowerCase()); });
    if (data.confAltele && data.confAlteleText) items.push(data.confAlteleText.toLowerCase());
    return items;
  }

  const confItems = getConfidentialItems();

  return (
    <div
      ref={ref}
      style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: isMobile ? "20px 16px" : "48px 52px",
        color: "#2d2418",
        border: "1px solid #e8e0d4",
        maxHeight: "600px",
        overflow: "auto",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        position: "relative",
      }}
    >
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "4px",
        background: "linear-gradient(90deg, #c45d3e, #a84832, #c45d3e)",
        borderRadius: "12px 12px 0 0",
      }} />

      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "24px",
          fontWeight: 800,
          color: "#2d2418",
          margin: "0 0 4px 0",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}>
          Acord de Confidentialitate (NDA)
        </h2>
        <p style={{
          fontSize: "13px",
          fontFamily: "'Georgia', serif",
          color: "#8a7e70",
          margin: 0,
        }}>
          Nr. ______ din {signDate}
        </p>
      </div>

      <hr style={{ border: "none", borderTop: "2px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* I. PARTILE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>I. PARTILE</h3>
        <p style={p}><span style={bold}>1.1. {p1Label}:</span></p>
        {renderParty("partea1")}
        <p style={{ ...p, color: "#8a7e70", fontStyle: "italic", margin: "16px 0" }}>si</p>
        <p style={p}><span style={bold}>1.2. {p2Label}:</span></p>
        {renderParty("partea2")}
        <p style={{ ...p, fontStyle: "italic", marginTop: "16px", fontSize: "13px" }}>
          denumite in continuare individual "Partea" si colectiv "Partile".
        </p>
      </div>

      {/* II. SCOPUL ACORDULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>II. SCOPUL ACORDULUI</h3>
        <p style={p}>
          <span style={bold}>2.1.</span> Prezentul acord reglementeaza protectia informatiilor confidentiale transmise intre Parti in cadrul: <span style={bold}>{data.scop || "________________________"}</span>.
        </p>
      </div>

      {/* III. DEFINITIA INFORMATIILOR CONFIDENTIALE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>III. DEFINITIA INFORMATIILOR CONFIDENTIALE</h3>
        <p style={p}>
          <span style={bold}>3.1.</span> Prin "Informatii Confidentiale" se inteleg toate informatiile, datele, documentele si materialele transmise de o Parte celeilalte, in orice forma (scrisa, verbala, electronica sau vizuala), inclusiv dar fara a se limita la:
        </p>
        {confItems.map((item, i) => (
          <p key={i} style={{ ...indent, paddingLeft: isMobile ? "20px" : "40px" }}>
            <span style={{ color: "#c45d3e", fontWeight: 600 }}>{"abcdefghij"[i]})</span> {item}
          </p>
        ))}
        <p style={{ ...p, marginTop: "12px" }}>
          <span style={bold}>3.2.</span> Nu sunt considerate Informatii Confidentiale:
        </p>
        {[
          "informatiile care sunt sau devin publice fara culpa Partii care le primeste",
          "informatiile care erau deja in posesia Partii care le primeste inainte de divulgare",
          "informatiile obtinute in mod independent de Partea care le primeste din surse terte care nu au obligatie de confidentialitate",
          "informatiile a caror divulgare este impusa prin lege sau de o autoritate competenta",
        ].map((text, i) => (
          <p key={i} style={{ ...indent, paddingLeft: isMobile ? "20px" : "40px" }}>
            <span style={{ color: "#c45d3e", fontWeight: 600 }}>{"abcd"[i]})</span> {text}
          </p>
        ))}
      </div>

      {/* IV. OBLIGATIILE PARTILOR */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>IV. OBLIGATIILE PARTILOR</h3>
        <p style={p}>
          <span style={bold}>4.1.</span> {data.bilateral ? "Fiecare Parte se obliga:" : "Partea care primeste se obliga:"}
        </p>
        {(data.bilateral ? [
          "sa utilizeze Informatiile Confidentiale primite exclusiv in scopul mentionat la art. 2.1",
          "sa nu divulge Informatiile Confidentiale ale celeilalte Parti catre terti fara acordul scris prealabil",
          "sa protejeze Informatiile Confidentiale primite cu cel putin acelasi grad de protectie pe care il aplica propriilor informatii confidentiale",
          "sa limiteze accesul la Informatiile Confidentiale doar la persoanele care au nevoie de aceste informatii pentru realizarea scopului acordului",
          "sa informeze imediat cealalta Parte in cazul oricarei divulgari neautorizate",
        ] : [
          "sa utilizeze Informatiile Confidentiale exclusiv in scopul mentionat la art. 2.1",
          "sa nu divulge Informatiile Confidentiale catre terti fara acordul scris prealabil al Partii care dezvaluie",
          "sa protejeze Informatiile Confidentiale cu cel putin acelasi grad de protectie pe care il aplica propriilor informatii confidentiale",
          "sa limiteze accesul la Informatiile Confidentiale doar la persoanele care au nevoie de aceste informatii pentru realizarea scopului acordului",
          "sa informeze imediat Partea care dezvaluie in cazul oricarei divulgari neautorizate",
        ]).map((text, i) => (
          <p key={i} style={{ ...indent, paddingLeft: isMobile ? "20px" : "40px" }}>
            <span style={{ color: "#c45d3e", fontWeight: 600 }}>{"abcde"[i]})</span> {text}
          </p>
        ))}
      </div>

      {/* V. DURATA */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>V. DURATA</h3>
        <p style={p}>
          <span style={bold}>5.1.</span> Prezentul acord intra in vigoare la data semnarii si este valabil <span style={bold}>{durataAcordLabels[data.durataAcord] || "pe durata colaborarii dintre Parti"}</span>.
        </p>
        <p style={p}>
          <span style={bold}>5.2.</span> Obligatia de confidentialitate se mentine pentru o perioada de <span style={bold}>{durataObligatieLabels[data.durataObligatie] || "2 (doi) ani"}</span> de la incetarea prezentului acord.
        </p>
      </div>

      {/* VI. RESTITUIREA INFORMATIILOR */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>VI. RESTITUIREA INFORMATIILOR</h3>
        <p style={p}>
          <span style={bold}>6.1.</span> La incetarea prezentului acord sau la cererea Partii care a dezvaluit informatiile, cealalta Parte se obliga sa restituie sau sa distruga toate materialele continand Informatii Confidentiale, inclusiv copiile acestora, in termen de 15 zile.
        </p>
      </div>

      {/* VII. RASPUNDERE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>VII. RASPUNDERE</h3>
        <p style={p}>
          <span style={bold}>7.1.</span> Fiecare Parte raspunde pentru prejudiciile cauzate celeilalte Parti prin incalcarea obligatiilor asumate prin prezentul acord.
        </p>
      </div>

      {/* Dynamic sections */}
      {(() => {
        const sections = [];
        let sNum = 8;
        let aNum = 8;

        if (data.penalitati) {
          const roman = toRoman(sNum);
          sections.push(
            <div key="penalitati" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{roman}. PENALITATI</h3>
              <p style={p}>
                <span style={bold}>{aNum}.1.</span> Incalcarea obligatiilor de confidentialitate prevazute in prezentul acord atrage obligatia Partii in culpa de a plati celeilalte Parti daune-interese in cuantum de <span style={bold}>{data.penalitateSuma || "___"} {data.penalitateMoneda || "EUR"}</span>.
              </p>
              <p style={p}>
                <span style={bold}>{aNum}.2.</span> Plata penalitatilor nu exonereaza Partea in culpa de obligatia de a repara integral prejudiciul cauzat prin incalcarea obligatiei de confidentialitate, daca acesta depaseste cuantumul penalitatilor.
              </p>
            </div>
          );
          sNum++;
          aNum++;
        }

        {
          const roman = toRoman(sNum);
          let instantaText = data.instanta === "bucuresti" ? "Tribunalul Bucuresti" :
            data.bilateral
              ? (data.instanta === "primeste" ? "instantele judecatoresti competente de la sediul/domiciliul Celei de-a Doua Parti" : "instantele judecatoresti competente de la sediul/domiciliul Primei Parti")
              : (data.instanta === "primeste" ? "instantele judecatoresti competente de la sediul/domiciliul Partii care primeste" : "instantele judecatoresti competente de la sediul/domiciliul Partii care dezvaluie");

          sections.push(
            <div key="litigii" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{roman}. LEGEA APLICABILA SI LITIGII</h3>
              <p style={indent}><span style={bold}>{aNum}.1.</span> Prezentul acord este guvernat de legea romana.</p>
              <p style={indent}><span style={bold}>{aNum}.2.</span> Litigiile se solutioneaza pe cale amiabila, iar in caz contrar de <span style={bold}>{instantaText}</span>.</p>
            </div>
          );
          sNum++;
          aNum++;
        }

        {
          const roman = toRoman(sNum);
          sections.push(
            <div key="finale" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
              <h3 style={sectionTitle()}>{roman}. DISPOZITII FINALE</h3>
              <p style={indent}><span style={bold}>{aNum}.1.</span> Orice modificare a prezentului acord se face prin act aditional semnat de ambele Parti.</p>
              <p style={indent}><span style={bold}>{aNum}.2.</span> Prezentul acord s-a incheiat in 2 exemplare originale, cate unul pentru fiecare Parte.</p>
            </div>
          );
        }

        return sections;
      })()}

      {/* Signatures */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: "48px",
        paddingTop: "32px",
        borderTop: "2px solid #e8e0d4",
        pageBreakInside: "avoid",
        breakInside: "avoid",
      }}>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {data.bilateral ? "PRIMA PARTE" : "PARTEA CARE DEZVALUIE"}
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {getPartyName(data, "partea1")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {signDate}
          </p>
        </div>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {data.bilateral ? "A DOUA PARTE" : "PARTEA CARE PRIMESTE"}
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {getPartyName(data, "partea2")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {signDate}
          </p>
        </div>
      </div>
    </div>
  );
});

export default ContractPreview;
